import { useQuery } from '@tanstack/react-query'
import { fetchAccessLog } from '@/api/client'
import { ApiError } from '@/api/client'
import { EmptyState } from '@/components/EmptyState'
import { ErrorState } from '@/components/ErrorState'
import { LoadingState } from '@/components/LoadingState'
import { formatDateTime } from '@/lib/format-date'

import { ScrollText } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'
function formatDetail(detail: Record<string, unknown> | null): string {
  if (!detail) return '—'
  const parts: string[] = []
  if (detail.year != null) parts.push(`year ${String(detail.year)}`)
  if (detail.view_code != null) parts.push(String(detail.view_code))
  if (detail.snapshot_id != null) parts.push(`snapshot ${String(detail.snapshot_id).slice(0, 8)}…`)
  if (detail.row_count != null) parts.push(`${String(detail.row_count)} rows`)
  if (parts.length === 0) return JSON.stringify(detail)
  return parts.join(' · ')
}

export function AccessLogAdminPage() {
  const logQuery = useQuery({
    queryKey: ['finance', 'access-log'],
    queryFn: () => fetchAccessLog(0, 200),
  })

  const items = logQuery.data?.items ?? []

  return (
    <AdminSection
      title="Access log"
      tone="slate"
      icon={ScrollText}
      lede="Forecast exports and reviewer sign-in / published snapshot views (BR-37)."
    >
      {logQuery.isLoading && (
        <LoadingState label="Loading access log…" variant="skeleton-table" rows={8} columns={4} />
      )}

      {logQuery.error && (
        <ErrorState
          message={
            logQuery.error instanceof ApiError
              ? logQuery.error.message
              : 'Could not load access log.'
          }
          onRetry={() => void logQuery.refetch()}
        />
      )}

      {!logQuery.isLoading && !logQuery.error && items.length === 0 && (
        <EmptyState title="No access events recorded yet." />
      )}

      {!logQuery.isLoading && items.length > 0 && (
        <div className="table-wrap">
          <table className="table table--dense">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Who</th>
                <th scope="col">Action</th>
                <th scope="col">Detail</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td>{formatDateTime(row.occurred_at)}</td>
                  <td>{row.user_email ?? row.user_id ?? '—'}</td>
                  <td>
                    <span className="badge badge--role badge--role-owner">{row.action}</span>
                  </td>
                  <td>{formatDetail(row.detail)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminSection>
  )
}
